import { AccountService } from "../../account/services/account.service";
import { parseList } from "@helpers/functions";

import { Injectable } from "@angular/core";
import { TradableListService } from "./currency/tradable_list.service";
import { BehaviorSubject, Observable } from "rxjs";
import { Tradable } from "@models/interfaces/tradable";

import { Index } from "@models/asset/index/index.model";
import { FiatService } from "./currency";
import { toCurrencyByFiatId } from "@helpers/tradable-convert";
import { Candle, Fiat } from "@models";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { Time } from "bp-common-models-lib";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Timespan } from "@helpers/const/timespan";

// TODO: eslint fix (Consider to remove unused function, all unused functions have any)
/* eslint-disable @typescript-eslint/no-explicit-any */
@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class TradableChartService {
	// Key format: Map<cryptocoinId, Map<timespan, BehaviorSubject<Candle[]>>>
	public observablePoints: Map<string, Map<string, BehaviorSubject<Candle[]>>> = new Map<
		string,
		Map<string, BehaviorSubject<Candle[]>>
	>();

	// The received empty array of candles from API. We store this, so we don't re-fetch each time if there are no candles for a coin.
	private receivedEmptyCandles: Map<string, boolean> = new Map<string, boolean>();

	public needReset: boolean = false;
	private lastFetchedFiat: Fiat = null; // We check this whenever Currency is changed from Account

	private callingTimespan: string = "";

	public constructor(
		private apicallService: ApicallService,
		private accountService: AccountService,
		private tradableListService: TradableListService,
		private translationService: TranslationService,
		private fiatService: FiatService
	) {
		this.initAutoUpdates();

		// User changes Currency in Account settings
		this.accountService.accountUpdated.subscribe(() => {
			const currentFiat = this.accountService.getDefaultFiat();
			if (this.lastFetchedFiat && this.lastFetchedFiat.getId() !== currentFiat.getId()) {
				this.reset();
			}
		});
	}

	public reset(): void {
		this.observablePoints = new Map<string, Map<string, BehaviorSubject<Candle[]>>>();
	}

	public calculateResolutionForTradingView(timespan: Timespan): string {
		switch (timespan) {
			case Timespan.Day:
				return "10";
			case Timespan.Week:
				return "60";
			case Timespan.Month:
				return "360";
			case Timespan.SixMonths:
				return "720";
			case Timespan.Year:
				return "1D";
			case Timespan.FiveYears:
				return "5D";
			default:
				console.error("TRADINGVIEW-ERROR", "calculateResolutionForTradingView", "unknown timespan");
				return "1D";
		}
	}

	/*
		Update with separate api call per each coin
	 */
	public updateChart(asset: Tradable, timespan: string): void {
		if (this.ongoingFullCall(timespan)) {
			return;
		}
		const a = new ApicallOption(Candle);
		const fiatSymbol: string =
			asset instanceof Index ? this.fiatService.getItemById("1").getSymbol() : this.fiat.getSymbol(); // Charts for Index should NEVER be converted, the correct value is always EUR
		this.apicallService
			.get("ohlc/" + asset.getPid() + "/" + fiatSymbol + "/" + timespan, a)
			.pipe(untilDestroyed(this))
			.subscribe(
				(ans: Candle[]) => {
					this.updateCandles(asset, timespan, ans);
				},
				(error) => console.error(error)
			);
	}

	/*
		Update multiple assets at once (specified on-demand)
	 */
	public updateMultiple(assets: Tradable[], timespan: string, resetEmpty: boolean = false): void {
		if (assets.length === 0) {
			return;
		}

		if (resetEmpty) {
			this.resetReceivedEmptyCandles();
		}

		// Define for which tradables we need to fetch new data
		const needCandleList = assets.filter((item) => {
			// Filter the ones for which we already received empty candles from API before
			if (this.receivedEmptyCandles.has(item.getId()) && !!this.receivedEmptyCandles.get(item.getId())) {
				return false;
			}
			// Filter the ones which already have candles. Keep those which have no candles so far
			let hasPoints = this.observablePoints.has(item.getId());
			if (hasPoints) {
				const map = this.observablePoints.get(item.getId());
				if (map.has(timespan)) {
					const timeMap = map.get(timespan);
					if (timeMap.getValue().length > 0) {
						return false;
					}
				}
			}
			return true;
		});

		// Data already exists for these tradables, so everything is filtered out. No need to do additional API call.
		if (needCandleList.length === 0) {
			return;
		}

		// API expects array of Pids as payload
		const pidList = needCandleList.map((item: Tradable) => item.getPid());

		// Pass params in options to encode them correctly
		const options = new ApicallOption();
		options.params = { "assets[]": pidList };

		// Call
		this.apicallService
			.get("ohlc/" + this.accountService.getDefaultFiat().getSymbol().toLowerCase() + "/" + timespan, options)
			.subscribe(
				(ans) => {
					this.tradableListService.list.forEach((asset) => {
						if (ans[asset.getPid().toUpperCase()]) {
							const candles = parseList(ans[asset.getPid().toUpperCase()], Candle);
							this.updateCandles(asset, timespan, candles);
						}
					});
				},
				(error) => console.error(error)
			);

		this.lastFetchedFiat = this.accountService.getDefaultFiat();
	}

	/*
		Update all coins per timespan
	 */
	public updateAll(timespan: string): void {
		if (this.callingTimespan === timespan) {
			return;
		}
		this.callingTimespan = timespan;
		this.apicallService.get("ohlc/" + this.accountService.getDefaultFiat().getSymbol() + "/" + timespan).subscribe(
			(ans) => {
				this.needReset = false;
				this.tradableListService.list.forEach((asset) => {
					if (ans[asset.getSymbol(true).toUpperCase()]) {
						const candles = parseList(ans[asset.getSymbol(true).toUpperCase()], Candle);
						this.updateCandles(asset, timespan, candles);
					}
				});
				this.callingTimespan = "";
			},
			(error) => console.error(error)
		);
	}

	public getUpdates(asset: Tradable, timespan: string): Observable<Candle[]> {
		if (!this.observablePoints.has(asset.getId())) {
			this.observablePoints.set(asset.getId(), new Map<string, BehaviorSubject<Candle[]>>());
		}

		const map = this.observablePoints.get(asset.getId());
		if (!map.has(timespan)) {
			map.set(timespan, new BehaviorSubject<Candle[]>([]));
		}
		return map.get(timespan).asObservable();
	}

	private get fiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public calculateValue(point: Candle): number {
		if (!this.fiat) {
			return 0;
		}
		return +(point.getClose() * this.fiat.toEuroRate()) / this.accountService.getDefaultFiat().toEuroRate();
	}

	private initAutoUpdates(): void {
		this.tradableListService.services.forEach((s) =>
			s
				.getPrice()
				.pipe(untilDestroyed(this))
				.subscribe((updated) => {
					updated.forEach((c) => {
						this.doAutoUpdate(c, Timespan.Day);
					});
				})
		);
	}

	private doAutoUpdate(c: Tradable, timespan: Timespan): void {
		const lastCandle: Candle = this.getLastCandle(c, timespan);

		if (lastCandle) {
			this.upsertCandle(lastCandle, c, timespan);
		}

		if (this.needApiUpdate(c, timespan)) {
			this.updateChart(c, timespan);
		}
	}

	private getCandles(asset: Tradable, timespan: string): BehaviorSubject<Candle[]> {
		const map = this.getAssetMap(asset, timespan);
		return map.get(timespan);
	}

	public needApiUpdate(c: Tradable, timespan: Timespan): boolean {
		// If 1 candle is missing we don't need an api update > the candle will be created dynamically with doAutoUpdate()
		// If 2 candles are missing then we need an api update > the candles will be updated with doCheckGap()

		const lastCandle = this.getLastCandle(c, timespan);
		if (lastCandle) {
			const res = this.calculateResolutionForTradingView(timespan);
			const rounded = this.getCurrentTimeCandle(res);
			const gap = rounded - lastCandle.getTime().getUnix();

			return gap >= this.resolutionInMS(res);
		}

		return false;
	}

	private getLastCandle(c: Tradable, timespan: string): Candle {
		const foundCandlesCoin = this.getCandles(c, timespan).getValue();
		if (foundCandlesCoin.length > 0) {
			return foundCandlesCoin[foundCandlesCoin.length - 1];
		}
		return null;
	}

	private pushCandle(c: Tradable, timespan: string, newCandle: Candle): void {
		const foundCandles = this.getCandles(c, timespan);
		const val = foundCandles.getValue();
		// Update or insert
		val.push(newCandle);
		foundCandles.next(val);
	}

	private resolutionInMS(res: string): number {
		let resolution: number = 60 * 1000;
		if (res.includes("D")) {
			resolution *= 1440; // 1 day in minutes
		} else if (res.includes("W")) {
			resolution *= 10080; // 1 week in minutes
		} else {
			resolution *= +res;
		}
		return resolution;
	}

	private getCurrentTimeCandle(res: string): number {
		const currentTime = new Time();
		currentTime.setByDate(new Date());
		const resInMSeconds = this.resolutionInMS(res);
		return Math.floor(currentTime.getUnix() / resInMSeconds) * resInMSeconds;
	}

	private upsertCandle(lastCandle: Candle, c: Tradable, timespan: Timespan): void {
		const res = this.calculateResolutionForTradingView(timespan);
		const rounded = this.getCurrentTimeCandle(res);

		const price = c.getAssetPrices() ? c.getAvgPrice() : +c.getSocketAvgPrice();
		const newPriceCurrency = toCurrencyByFiatId(price, this.accountService.getDefaultFiat());
		const newPriceCurrencyStr = "" + newPriceCurrency;
		// New candle
		const newCandleTime = new Time();
		newCandleTime.setByDate(new Date(rounded));

		const isNew = rounded > lastCandle.getTime().getUnix();

		const candle = new Candle();
		Object.assign(candle, {
			time: isNew ? newCandleTime : lastCandle.getTime(),
			open: isNew ? newPriceCurrencyStr : lastCandle.getOpen(),
			close: isNew ? newPriceCurrencyStr : lastCandle.getClose(),
			high: isNew ? newPriceCurrencyStr : Math.max(lastCandle.getHigh(), newPriceCurrency),
			low: isNew ? newPriceCurrencyStr : Math.min(lastCandle.getLow(), newPriceCurrency),
			id: isNew ? "" : lastCandle.getId(),
			volume: 0
		});

		if (rounded > lastCandle.getTime().getUnix()) {
			this.pushCandle(c, timespan, candle);
		} else {
			lastCandle = candle;
		}
	}

	public resetReceivedEmptyCandles(): void {
		this.receivedEmptyCandles = new Map<string, boolean>();
	}

	private updateCandles(asset: Tradable, timespan: string, ans: Candle[]): void {
		const map = this.getAssetMap(asset, timespan);
		this.receivedEmptyCandles.set(asset.getId(), ans.length === 0);
		map.get(timespan).next(ans);
	}

	private getAssetMap(asset: Tradable, timespan = null): Map<string, BehaviorSubject<Candle[]>> {
		if (!this.observablePoints.has(asset.getId())) {
			this.observablePoints.set(asset.getId(), new Map<string, BehaviorSubject<Candle[]>>());
		}

		const map = this.observablePoints.get(asset.getId());

		if (timespan) {
			if (!map.has(timespan)) {
				map.set(timespan, new BehaviorSubject<Candle[]>([]));
			}
		}

		return map;
	}

	private ongoingFullCall(timespan: string): boolean {
		return this.callingTimespan === timespan;
	}
}
